/**
 * Document scanning: the seam between "raw pixels become text" and everything
 * after it. Only this file knows about an engine; the parsers, validators and
 * the mapping below it are pure and never touch the camera or the network.
 *
 * One engine is wired now — Tesseract.js in the browser (the staff app runs in
 * the browser; there is no native shell). Apple Vision (iOS) or ML Kit
 * (Android) can replace it later behind this same interface, with nothing else
 * in this folder changed.
 */

/** A photographed side of a document, as the capture step hands it over. */
export interface ScanImage {
  /** The pixels. A Blob in the browser; the engine decides how to read it. */
  readonly blob: Blob
  /** Which side this is, so a two-sided read (licence) can keep them apart. */
  readonly side: 'front' | 'back'
}

/** One recognised line of text, where it sat, and how sure the engine was. */
export interface OcrLine {
  readonly text: string
  /** 0..1; a parser leans on this and never on the pixels. */
  readonly confidence: number
  /** The line's box in the source image, [x, y, width, height] in pixels. */
  readonly box: readonly [number, number, number, number]
}

/** Everything one engine read from one image. */
export interface OcrResult {
  readonly lines: readonly OcrLine[]
}

/** A read that was called off: the operator cancelled, or the dialog closed (OPS-1). */
export class ScanCancelled extends Error {
  constructor() {
    super('the scan was cancelled')
    this.name = 'ScanCancelled'
  }
}

/** A read the engine may be told to stop: it throws ScanCancelled once the signal fires. */
export interface ReadOptions {
  readonly signal?: AbortSignal
}

/**
 * The pluggable engine. `recognizeText` turns an image into lines; `readBarcode`
 * returns the raw payload of a 2D barcode (PDF417 on the back of US licences),
 * or null when there is none. An engine that cannot read barcodes returns null
 * and the US parser simply has nothing to work with. Both stop, with
 * ScanCancelled, when the signal they were given fires (OPS-1).
 */
export interface DocumentScanEngine {
  recognizeText(image: ScanImage, options?: ReadOptions): Promise<OcrResult>
  readBarcode(image: ScanImage, options?: ReadOptions): Promise<string | null>
}

/** Where a field's value came from; carried all the way to the form. */
export type FieldSource = 'licence' | 'mrz' | 'barcode' | 'manual'

/** A value the scan proposes, with how sure it is and where it came from. */
export interface ScanField<T = string> {
  readonly value: T
  /** 0..1. Below CONFIDENCE_FLOOR the operator must touch the field. */
  readonly confidence: number
  readonly source: FieldSource
}

/**
 * What a parser extracts. Every field is optional: a document carries some and
 * not others, OCR drops some, and the operator fills the rest by hand. Places
 * are plain names here (e.g. "ROMA"); the operator resolves each to a CaRGOS
 * TABELLA LUOGHI code through the existing PlacePicker — the scan never guesses
 * a code. Licence expiry, categories, issuing authority and the first-issue
 * date are read when present so `validate` can use them, even though v1 does
 * not put them on the counter's CaRGOS form (they live on the booking side).
 */
export interface ScannedIdentity {
  readonly surname?: ScanField
  readonly givenNames?: ScanField
  /** ISO yyyy-mm-dd. */
  readonly birthDate?: ScanField
  /** A place name, for the operator to resolve to a code. */
  readonly birthPlace?: ScanField
  /** ISO 3166-1 alpha-3 where known, otherwise the raw token. */
  readonly nationality?: ScanField
  /** CaRGOS document kind: 'IDELE' (ID card) or 'PASOR' (passport). Any other
   *  travel document is left for the operator to name (OPS-1). */
  readonly documentType?: ScanField
  /** Read when present but NOT mapped to the form: the ID number is typed by
   *  the operator and erased once CaRGOS has the receipt (Decision 2). */
  readonly documentNumber?: ScanField
  readonly licenceNumber?: ScanField
  /** ISO yyyy-mm-dd; field 4b. Used by `validate`, not shown on the v1 form. */
  readonly licenceExpiry?: ScanField
  /** ISO yyyy-mm-dd; field 4a. */
  readonly licenceIssued?: ScanField
  /** Field 4c, free text. */
  readonly licenceAuthority?: ScanField
  /** Field 9, e.g. ['A', 'B']. Used by the category check, not the form. */
  readonly licenceCategories?: ScanField<readonly string[]>
  /** ISO 3166-1 alpha-2 of the licence's country, read from the card's own
   *  title words where they name one country only (OPS-1). It keys the number
   *  format check and reaches no form field. */
  readonly issuingCountry?: ScanField
}

/** 0..1. A field at or above this is accepted; below it the UI makes the
 *  operator touch it before Conferma (the operator confirms everything, but
 *  low-confidence fields are highlighted and gated). */
export const CONFIDENCE_FLOOR = 0.6
